/**
 * AI CONTEXT
 * Purpose: this student's standout moments on the profile.
 * The bubble is only as wide as the note. The note is on top, the author
 * and date sit under it, and keyword tags sit to the right. A fixed slot
 * outside the bubble holds either the pending checkbox or a green checked
 * box for an approved row. Unchecking that box sets the row back to pending.
 * Used by: StudentProfileTabs.
 */
import { App, Button, Checkbox, Tag } from "antd"
import { keyBy, orderBy, uniq } from "lodash"
import { useCallback, useMemo, useState } from "react"
import styled from "styled-components"

import {
  classSessionAtoms,
  evaluationKeywordAtoms,
  standoutMomentAtoms,
  storyTimeAtoms,
} from "#atoms"
import Flex from "#blocks/Flex"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { otherFamilyItemsShared } from "#components/views/StandoutReview/standoutSessionGroups"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  type FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  type EvaluationKeywordConstructor,
  type StandoutCategory,
  type StandoutMomentConstructor,
} from "#customTypes/composedTypes/standoutTypes"
import { type StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import {
  useAllDocs,
  useFilteredDocs,
  useSomeDocs,
} from "#hooks/jotai/useSubscription"
import { useGetSomeUsers } from "#hooks/newHooks/useGetUser"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { COLORS } from "#styles/colors"
import {
  cancelFamilyDelivery,
  scheduleDeliveriesForMoments,
} from "#utils/familyDeliveryWrite"
import { deriveFamilyState } from "#utils/standoutMomentMigration"

import StandoutMomentBubble from "./StandoutMomentBubble"
import StandoutMomentContent from "./StandoutMomentContent"
import {
  buildFamilyApprovePatch,
  buildFamilyRevokePatch,
  FAMILY_REACTED_LABEL,
  isPendingFamilyApproval,
  momentHasFamilyReaction,
  NO_CLASS_SESSION_LABEL,
  SENT_TO_PARENTS_LABEL,
  standoutAuthorName,
  standoutMomentContent,
} from "./studentProfileTabHelpers"
import { useStudentFromForm } from "./useStudentFromForm"

const CATEGORY_COLORS: Record<StandoutCategory, string> = {
  thumbsUp: "#52c41a",
  needsWork: "#f5222d",
  secret: "#722ed1",
}

const CHECK_SLOT_PX = 16

const SApprovedCheckbox = styled(Checkbox)`
  && .ant-checkbox-checked .ant-checkbox-inner,
  && .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${COLORS.statuses.success};
    border-color: ${COLORS.statuses.success};
  }

  && .ant-checkbox-checked .ant-checkbox-inner::after,
  && .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: ${COLORS.text.WHITE};
  }
`

const StudentStandoutMomentsTab = () => {
  const { message } = App.useApp()
  const { studentId, student } = useStudentFromForm()
  const { currentUserId } = useUserContext()
  const canApprove = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )

  const momentsQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        studentId: {
          value: studentId ?? NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [studentId]
  )

  const moments = useFilteredDocs<StandoutMomentConstructor>(
    standoutMomentAtoms,
    DOC_TYPES.STANDOUT_MOMENTS,
    momentsQuery
  )
  const sortedMoments = useMemo(
    () => orderBy(moments, (moment) => moment.createdAt, "desc"),
    [moments]
  )
  const sessionIds = useMemo(
    () =>
      uniq(
        sortedMoments.flatMap((moment) =>
          moment.classSessionId ? [moment.classSessionId] : []
        )
      ).sort(),
    [sortedMoments]
  )
  const classSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionIds
  )
  const storyTimeQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        studentId: {
          value: studentId ?? NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [studentId]
  )
  const storyTimes = useFilteredDocs<StoryTimeConstructor>(
    storyTimeAtoms,
    DOC_TYPES.STORY_TIMES,
    storyTimeQuery
  )
  const classImages = useMemo(() => {
    if (!studentId) return []
    return Object.values(student?.artwork ?? {}).flatMap((artwork) =>
      artwork?.id ? [{ studentId, artwork }] : []
    )
  }, [student?.artwork, studentId])
  const eventEndBySessionId = useMemo(() => {
    const map: Record<string, string | undefined> = {}
    for (const session of classSessions) map[session.id] = session.eventEnd
    return map
  }, [classSessions])

  const keywords = useAllDocs<EvaluationKeywordConstructor>(
    evaluationKeywordAtoms,
    DOC_TYPES.EVALUATION_KEYWORDS
  )
  const keywordsById = useMemo(() => keyBy(keywords, "id"), [keywords])

  const authorUserIds = useMemo(
    () => uniq(sortedMoments.map((moment) => moment.createdBy)).sort(),
    [sortedMoments]
  )
  const assistantIds = useMemo(
    () =>
      uniq(
        sortedMoments.flatMap((moment) =>
          moment.assistantId ? [moment.assistantId] : []
        )
      ).sort(),
    [sortedMoments]
  )
  const authors = useGetSomeUsers(authorUserIds)
  const taStudents = useGetSomeStudents(assistantIds)
  const usersById = useMemo(() => keyBy(authors, "id"), [authors])
  const studentsById = useMemo(() => keyBy(taStudents, "id"), [taStudents])

  const pendingIds = useMemo(
    () =>
      sortedMoments.filter(isPendingFamilyApproval).map((moment) => moment.id),
    [sortedMoments]
  )
  const pendingIdSet = useMemo(() => new Set(pendingIds), [pendingIds])

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const selectedPendingIds = useMemo(
    () => selectedIds.filter((id) => pendingIdSet.has(id)),
    [pendingIdSet, selectedIds]
  )

  const allPendingSelected =
    pendingIds.length > 0 && selectedPendingIds.length === pendingIds.length
  const somePendingSelected =
    selectedPendingIds.length > 0 && !allPendingSelected

  const [isApproving, setIsApproving] = useState(false)
  const [revokingId, setRevokingId] = useState<string | null>(null)

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? pendingIds : [])
    },
    [pendingIds]
  )

  const handleToggleOne = useCallback((momentId: string, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...current, momentId] : current.filter((id) => id !== momentId)
    )
  }, [])

  const handleApprove = useCallback(async () => {
    if (!currentUserId || selectedPendingIds.length === 0) return
    setIsApproving(true)
    const sharedAt = new Date().toISOString()
    try {
      await Promise.all(
        selectedPendingIds.map((momentId) => {
          const moment = sortedMoments.find((entry) => entry.id === momentId)
          return Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STANDOUT_MOMENTS,
            momentId,
            buildFamilyApprovePatch(
              currentUserId,
              sharedAt,
              moment?.sharedBy,
              moment
            )
          )
        })
      )
    } catch (error) {
      void message.error(
        error instanceof Error
          ? error.message
          : "Could not approve these moments."
      )
      setIsApproving(false)
      return
    }
    const approved = selectedPendingIds.flatMap((momentId) => {
      const moment = sortedMoments.find((entry) => entry.id === momentId)
      return moment ? [moment] : []
    })
    try {
      await scheduleDeliveriesForMoments(approved, eventEndBySessionId)
    } catch (error) {
      console.error("[standout] schedule family delivery failed", error)
      void message.error(
        "Saved the share, but the send timer did not start. Try again."
      )
    }
    setSelectedIds([])
    setIsApproving(false)
  }, [
    currentUserId,
    eventEndBySessionId,
    message,
    selectedPendingIds,
    sortedMoments,
  ])

  const handleRevoke = useCallback(
    async (momentId: string) => {
      if (!currentUserId) return
      setRevokingId(momentId)
      const sharedAt = new Date().toISOString()
      const moment = sortedMoments.find((entry) => entry.id === momentId)
      try {
        await Firebase.getInstance().updateDocumentOrThrow(
          DOC_TYPES.STANDOUT_MOMENTS,
          momentId,
          buildFamilyRevokePatch(currentUserId, sharedAt, moment?.sharedBy)
        )
      } catch (error) {
        void message.error(
          error instanceof Error
            ? error.message
            : "Could not revoke this approval."
        )
        setRevokingId(null)
        return
      }
      if (moment?.studentId && moment.classSessionId) {
        const othersStillShared = otherFamilyItemsShared(
          { moments: sortedMoments, storyTimes, classImages },
          moment.studentId,
          moment.classSessionId,
          { kind: "moment", id: moment.id }
        )
        if (!othersStillShared) {
          try {
            await cancelFamilyDelivery(moment.studentId, moment.classSessionId)
          } catch (error) {
            console.error("[standout] cancel family delivery failed", error)
            void message.error(
              "Unshared the note, but the send timer is still running. Try again."
            )
          }
        }
      }
      setRevokingId(null)
    },
    [classImages, currentUserId, message, sortedMoments, storyTimes]
  )

  return (
    <div
      data-testid="student-standout-moments-tab"
      style={{ height: 900, overflowY: "auto" }}
    >
      <Flex col gap={12}>
        {canApprove && pendingIds.length > 0 ? (
          <Flex gap={12} style={{ alignItems: "center" }}>
            <Checkbox
              checked={allPendingSelected}
              indeterminate={somePendingSelected}
              onChange={(event) => handleToggleAll(event.target.checked)}
              data-testid="standout-select-pending"
            >
              Select pending
            </Checkbox>
            <Button
              type="primary"
              size="small"
              disabled={!currentUserId || selectedPendingIds.length === 0}
              loading={isApproving}
              onClick={() => void handleApprove()}
              data-testid="standout-approve"
            >
              Approve
            </Button>
          </Flex>
        ) : null}
        {sortedMoments.length === 0 ? (
          <div>No standout moments yet.</div>
        ) : (
          sortedMoments.map((moment) => {
            const familyState = deriveFamilyState(moment)
            const tagColor = moment.category
              ? CATEGORY_COLORS[moment.category]
              : COLORS.GRAY5
            const selectable = pendingIdSet.has(moment.id)
            const approved = familyState === "approved"
            const sent = familyState === "sent"
            const note = standoutMomentContent(moment).lines.find(
              (line) => line.key === "note"
            )?.text
            const authorName = standoutAuthorName(
              moment,
              usersById,
              studentsById
            )
            const tagIds = moment.tagIds ?? []
            return (
              <div
                key={moment.id}
                data-testid="profile-standout-moment"
                data-moment-id={moment.id}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                }}
              >
                <div
                  data-testid="standout-check-slot"
                  style={{
                    width: CHECK_SLOT_PX,
                    flex: `0 0 ${CHECK_SLOT_PX}px`,
                    marginTop: 10,
                  }}
                >
                  {approved || sent ? (
                    <SApprovedCheckbox
                      checked
                      disabled={sent || !canApprove || revokingId === moment.id}
                      onChange={(event) => {
                        if (!event.target.checked) {
                          void handleRevoke(moment.id)
                        }
                      }}
                      data-testid="standout-approved-checkbox"
                    />
                  ) : selectable && canApprove ? (
                    <Checkbox
                      checked={selectedPendingIds.includes(moment.id)}
                      onChange={(event) =>
                        handleToggleOne(moment.id, event.target.checked)
                      }
                      data-testid="standout-moment-checkbox"
                    />
                  ) : null}
                </div>
                <StandoutMomentBubble
                  note={note}
                  authorName={authorName}
                  createdAt={moment.createdAt}
                  maxWidth="calc(100% - 26px)"
                  tags={tagIds.map((tagId) => ({
                    id: tagId,
                    text: keywordsById[tagId]?.text ?? tagId,
                    color: tagColor,
                  }))}
                  extra={
                    sent ||
                    (moment.category !== "secret" &&
                      !moment.classSessionId &&
                      familyState !== "sent") ? (
                      <Flex gap={4} style={{ flexWrap: "wrap" }}>
                        {sent ? (
                          <Tag color="green">{SENT_TO_PARENTS_LABEL}</Tag>
                        ) : null}
                        {sent && momentHasFamilyReaction(moment) ? (
                          <Tag>{FAMILY_REACTED_LABEL}</Tag>
                        ) : null}
                        {moment.category !== "secret" &&
                        !moment.classSessionId &&
                        familyState !== "sent" ? (
                          <Tag data-testid="standout-no-session">
                            {NO_CLASS_SESSION_LABEL}
                          </Tag>
                        ) : null}
                      </Flex>
                    ) : null
                  }
                >
                  <StandoutMomentContent moment={moment} omitKeys={["note"]} />
                </StandoutMomentBubble>
              </div>
            )
          })
        )}
      </Flex>
    </div>
  )
}

export default StudentStandoutMomentsTab
